import { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';

import { ApplicationState } from 'store/redux';
import { IAuthenticationActionCreators, LoginState, UserState } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import Context from 'configuration/context';
import { IUserService } from 'services/userService';
import userServiceFactory from 'services/userService';
import { authenticationServiceFactory } from 'services/authenticationService';
import { ICustomerReference } from 'models/User';
import { ISiteReference, IUserInRole } from 'pages/Login';
import './Profile.css';
import ResetMfaModal from 'components/modals/ResetMfaModal';
import CustomerMultiSelect from 'components/form/CustomerMultiSelect';

const Profile = function (props: Props) {
  const { t } = useTranslation('translation');
  const [isResetMfaOpen, setIsResetMfaOpen] = useState(false);

  const loginState = props.UserState?.LoginState;

  const userService: IUserService = userServiceFactory(
    Context.APIUrl + '/v1',
    authenticationServiceFactory(
      Context.APIUrl,
      { setLoginState: props.setLoginState },
      props.UserState
    )
  );

  const onCustomerChange = (customer: ICustomerReference) => {
    if (!props.OnActiveCustomerSelect) return;

    const knownCustomer = props.UserState.UserInRole?.availableCustomers?.find(
      (c) => c.customerId.toString() === customer.id.toString()
    );

    props.OnActiveCustomerSelect(
      knownCustomer ?? {
        customerName: customer.name,
        customerId: customer.id.toString(),
        invoicesAreAvailable: false,
        orderServiceIsAvailable: false,
        movieServiceIsAvailable: false,
        reportServiceIsAvailable: false,
      }
    );

    props.OnClose();
  };

  if (!loginState) return null;
  if (loginState !== LoginState.LOGGED_IN) return null;
  if (!props.UserState.UserInRole) return null;

  const UserInRole: IUserInRole = props.UserState.UserInRole;
  const UserIsAdmin: boolean = UserInRole.role === 'avonovaStaff';
  let availableCustomers: ISiteReference[] = UserInRole.availableCustomers
    ? UserInRole.availableCustomers
    : [];

  const availableCustomerOptions: ICustomerReference[] = availableCustomers.map((customer) => ({
    id: Number(customer.customerId),
    name: customer.customerName,
  }));
  const selectedCustomerOption = availableCustomerOptions.find(
    (option) => option.id.toString() === UserInRole.selectedCustomer?.customerId?.toString()
  );

  return (
    <>
      <h1>{t('profile.YOUR_PROFILE')}</h1>
      <h2>
        {UserInRole.name}{' '}
        <Button
          className="logout-button btn-primary"
          onClick={() => {
            props.OnLogout && props.OnLogout();
          }}
        >
          {t('profile.LOGOUT')}
        </Button>
      </h2>
      <div>
        {UserIsAdmin && (
          <>
            <span>
              {t('profile.ACTIVE_CUSTOMER')}: {UserInRole?.selectedCustomer?.customerName}
            </span>
            <p>{t('profile.ADMIN_SELECT_CUSTOMER_INSTRUCTIONS')}</p>
            <CustomerMultiSelect
              onChange={onCustomerChange}
              searchCustomers={userService.SearchForCustomersReference}
            />
          </>
        )}
        {!UserIsAdmin && (
          <>
            {!props.UserState.IsSsoUser && (
              <div className="new-mfa-account-link">
                <a href="#" onClick={() => setIsResetMfaOpen(true)}>
                  {t('profile.resetMfaLink')}
                </a>
              </div>
            )}
            {availableCustomers.length === 1 && (
              <span>
                {t('profile.CUSTOMER')}: {UserInRole?.selectedCustomer?.customerName}
              </span>
            )}
            {availableCustomers.length > 1 && (
              <>
                <span>
                  {t('profile.ACTIVE_CUSTOMER')}: {UserInRole?.selectedCustomer?.customerName}
                </span>
                <p>{t('profile.USER_SELECT_CUSTOMER_INSTRUCTIONS')}</p>
                <CustomerMultiSelect
                  onChange={onCustomerChange}
                  options={availableCustomerOptions}
                  value={selectedCustomerOption}
                />
              </>
            )}
          </>
        )}
      </div>
      <ResetMfaModal
        open={isResetMfaOpen}
        userState={props.UserState}
        onClose={() => setIsResetMfaOpen(false)}
        logout={props.OnLogout}
      />
    </>
  );
};

type Props = IAuthenticationActionCreators & {
  UserState: UserState;
  OnClose: () => void;
  OnLogout?: () => void;
  OnActiveCustomerSelect?: (selectedSiteReference: ISiteReference) => void;
};

export default connect((appState: ApplicationState) => ({ UserState: appState.authentication }), {
  ...AuthenticationStore.actions,
})(Profile);
